import { useState, useEffect, useContext } from 'react';
import { getDisabled } from '@/components/UserInfoForm/helpers';
import { useNavigate, useParams } from 'react-router';
import useStore from '@/store/main/action';
import _ from 'lodash';
import { API_URL } from '@/constants';
import { toast } from 'sonner';
import { simpleFetch } from '@/utils/simpleFetch';
import { AuthContext } from '@panamod/react-utils';

export interface EncuestaInfo {
    nEncuesta: number;
    cDescripcion: string;
    cMensajeInicial: string;
    Preguntas: Pregunta[];
    PreguntaRespuestas: PreguntaRespuesta[];
    PreguntasDetalles: PreguntasDetalle[];
    Assets: Assets;
    MetaInfo: MetaInfo[];
    Headers: MetaInfo[];
    TokenExtraInfo: TokenExtraInfo[];
}

export interface Assets {
    cLogo: string;
    cBackgroundImage: string;
    cPrimaryColor: string;
    cSecondaryColor: string;
    cMetaInfoTitulo: string;
}

export interface MetaInfo {
    nEncuestaMetaInfo: number;
    cNombreMetaInfo: string;
    cValor?: string
    nTipo: number;
    nPregunta: number | null;
    bOpcional: boolean;
    nEncuestaMetaInfoDepende: number | null
}

export interface PreguntaRespuesta {
    nPregunta: number;
    nRespuesta: number;
    cDescripcion: string;
    nValor: number;
    cUrl?: string
}

// export interface Pregunta {
export interface Pregunta {
    cSeccion: string;
    nPregunta: number;
    cPregunta: string;
    nTipo: number;
    cValor?: string
    cRespuesta?: string
    nRespuesta?: number;
    bOpcional: boolean | null
    bMostrarSeccion: boolean | null;
}

export interface PreguntasDetalle {
    nPregunta: number;
    nDetalle: number;
    cDescripcion: string;
    nValor?: string
}

export interface Select {
    nEncuesta: number
    nEncuestaMetaInfo: number
    nRespuesta: number
    cRespuesta: string
    bActivo: string
    dFecha_Registro: string
    cUsuario_Registro: string
}

interface fetchSelects {
    Select: Select[]
}
interface props {
    validarPorToken?: boolean
}

export type SurveyStepsHandler = <K extends keyof Pregunta>(i: number, index: K, value: Pregunta[K]) => void;
export type SurveyStepsDetailsHandler = <K extends keyof PreguntasDetalle>(i: number, index: K, value: PreguntasDetalle[K]) => void;

export interface TokenExtraInfo {
    nExtraInfo: number;
    nTipo: number;
    cValor: string;
}

const useSurvey = ({ validarPorToken }: props) => {
    const { setAssets, primaryColor, removeAssets, icon, setSurveyToken, cDescripcion } = useStore();

    const { handleValidarSesion: handleValidaSesion } = useContext(AuthContext)

    const navigate = useNavigate();
    const handleMenuPrincipal = () => {
        navigate(`/`);
    };

    const { encuestaId, encuestaToken } = useParams();

    const [dataEncuestas, setdataEncuestas] = useState<EncuestaInfo>();
    const [surveySteps, setsurveySteps] = useState<Pregunta[]>([]);

    const [surveyStepsDetails, setsurveyStepsDetails] = useState<PreguntasDetalle[]>([]);

    const [status, setstatus] = useState(0);
    const [loading, setloading] = useState(false);
    const [metaInfo, setmetaInfo] = useState<MetaInfo[]>([]);
    const [TokenExtraInfo, setTokenExtraInfo] = useState<TokenExtraInfo[]>([]);
    const [headers, setheaders] = useState<MetaInfo[]>([]);
    const [surveyLimitReached, setSurveyLimitReached] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const handleReset = () => {
        
        if (status !== 0) {
            const copy = [...surveySteps];
            copy.forEach((element) => {
                element.cValor = undefined;
                element.nRespuesta = undefined;
            });
            setsurveySteps(copy);

            const copyheaders = [...headers];
            copyheaders.forEach((element) => {
                element.cValor = undefined;
            });
            setheaders(copyheaders);

            const copyMetaInfo = [...metaInfo];
            copyMetaInfo.forEach((element) => {
                element.cValor = undefined;
            });
            setmetaInfo(copyMetaInfo);

            setstatus(headers.length > 0 ? -1 : 0);
        }
    };

    const handleObtenerSelect = async(nEncuesta: number, nEncuestaMetaInfo:number, nRespuestaMetaInfoDepende:number | null) => {
        try {
            const response = await simpleFetch<fetchSelects>({
                // Fijo en v1: ver nota junto a handleGetEncuesta.
                url: validarPorToken ? `${API_URL}v1/Obten/ComboxToken` : `${API_URL}v1/Obten/Combo`,
                method: 'POST',
                headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
                params: {
                    nEncuesta, nEncuestaMetaInfo, nRespuestaMetaInfoDepende
                },
            })

            if(response.success){
                return response.data.Select
            }else{
                toast.error(`Sucedió un error`);
            }
        } catch (error) {
            setstatus(3)
        }
    }

    const handleGetEncuesta = async () => {
        try {
            // Fijo en v1: el path sin versión de Obten/Encuesta(xToken) ya
            // apunta al SP nuevo (Reactivos/Secciones/ReactivosOpciones), y
            // este hook todavía consume el shape viejo (Preguntas,
            // PreguntaRespuestas, PreguntasDetalles, Assets, Headers). No
            // mover hasta remapear SurveyWizard/UserInfoForm al shape nuevo.
            const response = await simpleFetch<EncuestaInfo>({
                url: validarPorToken ? `${API_URL}v1/Obten/EncuestaxToken/${encuestaToken}` : `${API_URL}v1/Obten/Encuesta/${encuestaId}`,
                method: 'POST',
                headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
            })

            setloading(false)

            if (!response.success) {
                setErrorMessage(response.message);
                setstatus(response?.code === 'NOT_FOUND' ? 5 : 3);
                return;
            }
            const encuesta = response.data;
            setdataEncuestas(encuesta);

            // const endpoint = `${urlLocal}Encuestas/Uploads?file=`;

            if (encuesta?.Headers?.length > 0) {
                setheaders(encuesta.Headers);
                setstatus(-1);
            }

            encuesta.MetaInfo && setmetaInfo(encuesta.MetaInfo);

            encuesta.Assets &&
                setAssets(
                    `#${encuesta.Assets.cPrimaryColor}`,
                    `${encuesta.Assets.cLogo}`,
                    `${encuesta.Assets.cBackgroundImage}`,
                    encuesta.cDescripcion,
                );

            if (Array.isArray(encuesta.Preguntas) && encuesta.Preguntas.length !== 0) {
                setsurveySteps([...encuesta.Preguntas]);
            } else {
                setstatus(5);
                setSurveyToken('');
            }

            encuesta.PreguntasDetalles && setsurveyStepsDetails([...encuesta.PreguntasDetalles]);
            encuesta.TokenExtraInfo && setTokenExtraInfo([...encuesta.TokenExtraInfo]);

        } catch (error) {
            setstatus(3)
        }
        
    }

    useEffect(() => {
        removeAssets();

        if (encuestaId ?? encuestaToken) {
            setSurveyToken(encuestaId);

            // simpleFetch<EncuestaInfo>({
            //     url: validarPorToken ? `${API_URL}Obten/EncuestaxToken?cCodigo=${encuestaToken}` : `${API_URL}Obten/Encuesta?cCodigo=${encuestaId}`,
            //     method: 'POST',
            //     headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
            // }).then((response) => {
            //     setloading(false);

            //     if (!response.success) {
            //         setstatus(response && response.data?.[0] === 'not found' ? 5 : 3);
            //         return;
            //     }

            //     const encuesta = response.data;
            //     setdataEncuestas(encuesta);

            //     // const endpoint = `${urlLocal}Encuestas/Uploads?file=`;

            //     if (encuesta?.Headers?.length > 0) {
            //         setheaders(encuesta.Headers);
            //         setstatus(-1);
            //     }

            //     encuesta.MetaInfo && setmetaInfo(encuesta.MetaInfo);

            //     encuesta.Assets &&
            //         setAssets(
            //             `#${encuesta.Assets.cPrimaryColor}`,
            //             `${encuesta.Assets.cLogo}`,
            //             `${encuesta.Assets.cBackgroundImage}`,
            //             encuesta.cDescripcion,
            //         );

            //     if (Array.isArray(encuesta.Preguntas) && encuesta.Preguntas.length !== 0) {
            //         setsurveySteps([...encuesta.Preguntas]);
            //     } else {
            //         setstatus(5);
            //         setSurveyToken('');
            //     }

            //     encuesta.PreguntasDetalles && setsurveyStepsDetails([...encuesta.PreguntasDetalles]);
            // })
            // .catch((error) => {
            //     console.log(error, 'error');
            //     setstatus(3);
            // });
            
            handleGetEncuesta()
            // simpleFetch<EncuestaInfo>({
            //     url: validarPorToken ? `${API_URL}Obten/EncuestaxToken?cCodigo=${encuestaToken}` : `${API_URL}Obten/Encuesta?cCodigo=${encuestaId}`,
            //     method: 'POST',
            //     headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
            // }).then((response) => {
            //         setloading(false);

            //         if (!response.success) {
            //             setstatus(response.data === 'not found' ? 5 : 3);
            //             return;
            //         }

            //         const encuesta = response.data;
            //         setdataEncuestas(encuesta);

            //         // const endpoint = `${urlLocal}Encuestas/Uploads?file=`;

            //         if (encuesta?.Headers?.length > 0) {
            //             setheaders(encuesta.Headers);
            //             setstatus(-1);
            //         }

            //         encuesta.MetaInfo && setmetaInfo(encuesta.MetaInfo);

            //         encuesta.Assets &&
            //             setAssets(
            //                 `#${encuesta.Assets.cPrimaryColor}`,
            //                 `${encuesta.Assets.cLogo}`,
            //                 `${encuesta.Assets.cBackgroundImage}`,
            //                 encuesta.cDescripcion,
            //             );

            //         if (Array.isArray(encuesta.Preguntas) && encuesta.Preguntas.length !== 0) {
            //             setsurveySteps([...encuesta.Preguntas]);
            //         } else {
            //             setstatus(5);
            //             setSurveyToken('');
            //         }

            //         encuesta.PreguntasDetalles && setsurveyStepsDetails([...encuesta.PreguntasDetalles]);
            //     })
            //     .catch((error) => {
            //         console.log(error, 'error');
            //         setstatus(3);
            //     });
        }
    }, [encuestaId, encuestaToken]);

    const handleSurveySteps: SurveyStepsHandler = (i, index, value) => {
        const copy = [...surveySteps];
        copy[i][index] = value;
        setsurveySteps(copy);
    };

    const handleSurveyStepsDetails: SurveyStepsDetailsHandler = (i, index, value) => {
        const copy = [...surveyStepsDetails];
        copy[i][index] = value;
        setsurveyStepsDetails(copy);
    };

    const handleMetaInfo = (i: number, value: string) => {
        const copy = [...metaInfo];
        copy[i].cValor = value;
        setmetaInfo(copy);
    };
    const disabledStart = getDisabled(metaInfo);

    const handleHeaders = (i: number, value: string) => {
        const copy = [...headers];
        copy[i].cValor = value;
        setheaders(copy);
    };
    const disabledStartHeaders = getDisabled(headers);

    const handleDone = async () => {
        setloading(true);
        if (!disabledStart) {
            try {
                const datos = {
                    IDEncuesta: dataEncuestas?.nEncuesta,
                    MetaData: [...headers, ..._.filter(metaInfo, (o) => o.nTipo !== 2)],
                    Respuestas: surveySteps,
                    RespuestasDetalles: surveyStepsDetails,
                    emailImg: icon,
                    primaryColor,
                    cDescripcion,
                };
                //console.log(dataEncuestas.nEncuesta)
                // Fijo en v1: ver nota junto a handleGetEncuesta.
                const { success, data } = await simpleFetch({
                    url: validarPorToken ? `${API_URL}v1/Guarda/RespuestasxToken` : `${API_URL}v1/Guarda/Respuestas`,
                    params: datos,
                    method: 'POST',
                    headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
                });

                if (success) {
                    setstatus(2);
                    const responseData = data as { nUsosMaximo?: number; nUsos?: number };
                    console.log(data)
                    if (responseData?.nUsosMaximo != null && responseData.nUsosMaximo >= 1 && responseData?.nUsos === responseData.nUsosMaximo) {
                        setSurveyLimitReached(true);
                    }
                } else {
                    toast.error('Sucedió un error');

                    simpleFetch({
                        url: `${API_URL}v1/Guarda/LogError`,
                        params: { success, data },
                        method: 'POST',
                        headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
                    });
                }

                setloading(false);
            } catch (e) {
                console.log(e, 'error');

                await simpleFetch({
                    url: `${API_URL}v1/Guarda/LogError`,
                    params: { Error: String(e) },
                    method: 'POST',
                    headers: validarPorToken ? { Token: encuestaToken ?? '' } : undefined,
                });

                setstatus(3);
            }
        }
    };

    // const handleValidaSesion = async () => {    
    //     try {
    //         const { response } = await customFetch({
    //             url: `${SSO_API_URL}/Valida/Sesion`,
    //             method: 'POST',
    //             // El header Authorization lo agrega customFetch de forma centralizada (utils/Auth.ts)
    //             headers: validarPorToken ? {} : undefined
    //         });

    //         if (!response.Success) {
    //             setIsLogged(false);
    //             throw 'Sesión Inválida';
    //         }

    //         setUser(response.Data);
    //         setUserInfo(response.Data);
    //         setIsLogged(true);
    //     } catch (error) {
    //         handleRedirectSSO();
    //     }
    // };

    return {
        surveySteps,
        handleSurveySteps,
        handleSurveyStepsDetails,
        handleDone,
        status,
        loading,
        handleReset,
        setstatus,
        handleMenuPrincipal,
        handleMetaInfo,
        metaInfo,
        primaryColor,
        disabledStart,
        dataEncuestas,
        headers,
        handleHeaders,
        disabledStartHeaders,
        handleValidaSesion,
        handleObtenerSelect,
        surveyLimitReached,
        TokenExtraInfo,
        errorMessage
    };
};

export default useSurvey;
